/**
 * DeleteSiteDialog — site-level cascade confirmation. Mirrors
 * DeleteReportDialog (Workflow Prompt 7) one level up: a site can have
 * several reports, each with their own SED, uploaded documents, and
 * simulations. Surfaces what will be removed before the user commits
 * and requires a typed-name confirmation.
 *
 * Modes: 'site-only' | 'with-reports' | 'with-everything'.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useStore } from '../../store';
import { AlertTriangle, X, Building2 } from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import type { SiteEntity } from '../../types/site';
import {
  computeSiteDeleteImpact,
  formatBytes,
  type DeleteSiteMode,
  type DeleteSiteResult,
} from '../../services/cascadeDelete';

interface Props {
  site: SiteEntity;
  open: boolean;
  onClose: () => void;
  /** Called after the cascade completes (success OR partial failure). */
  onDeleted?: (result: DeleteSiteResult) => void;
  /** Called when the user clicks the "Delete the orphaned reports →" toast link
   *  after a 'site-only' delete. The host decides what to do (admin bulk delete,
   *  navigate to a filtered reports view, etc.). */
  onCleanupOrphans?: (orphanReportIds: string[]) => void;
}

type Stage = 'idle' | 'documents' | 'seds' | 'reports' | 'simulations' | 'site' | 'done';

const STAGE_LABEL: Record<Stage, string> = {
  idle: '',
  documents: 'Deleting documents…',
  seds: 'Deleting evidence…',
  reports: 'Deleting reports…',
  simulations: 'Deleting simulations…',
  site: 'Deleting site…',
  done: '',
};

export const DeleteSiteDialog: React.FC<Props> = ({ site, open, onClose, onDeleted, onCleanupOrphans }) => {
  const allReports = useStore.getState().siteRag.reports;
  const [mode, setMode] = useState<DeleteSiteMode>('site-only');
  const [confirmText, setConfirmText] = useState('');
  const [busy, setBusy] = useState(false);
  const [stage, setStage] = useState<Stage>('idle');

  // Pending approval workflow gate. Loaded async on mount; if any report has
  // an in-review workflow, the dialog refuses to mount the action UI.
  const [approvalCheck, setApprovalCheck] = useState<{ loading: boolean; blocked: boolean; reportTitles: string[] }>({
    loading: true, blocked: false, reportTitles: [],
  });

  const impact = useMemo(
    () => computeSiteDeleteImpact(site, allReports),
    [site, allReports],
  );

  useEffect(() => {
    if (!open) return;
    setMode('site-only');
    setConfirmText('');
    setBusy(false);
    setStage('idle');
    setApprovalCheck({ loading: true, blocked: false, reportTitles: [] });

    let cancelled = false;
    (async () => {
      try {
        const { auth, db } = await import('../../firebase');
        const user = auth.currentUser;
        if (!user || impact.reports.length === 0) {
          if (!cancelled) setApprovalCheck({ loading: false, blocked: false, reportTitles: [] });
          return;
        }
        const { doc, getDoc } = await import('firebase/firestore');
        const titles: string[] = [];
        for (const r of impact.reports) {
          try {
            const snap = await getDoc(doc(db, 'approval_workflows', r.id));
            if (snap.exists()) {
              const wf = snap.data() as { status?: string; reportTitle?: string };
              if (wf.status === 'in-review') {
                titles.push(wf.reportTitle || `${r.siteName} (${r.id})`);
              }
            }
          } catch { /* best-effort — skip per-report errors */ }
        }
        if (!cancelled) {
          setApprovalCheck({ loading: false, blocked: titles.length > 0, reportTitles: titles });
        }
      } catch {
        if (!cancelled) setApprovalCheck({ loading: false, blocked: false, reportTitles: [] });
      }
    })();
    return () => { cancelled = true; };
  }, [open, site.id, impact.reports]);

  if (!open) return null;

  // FIX #85 (part 2) — nameless phantom sites can't be confirmed by name:
  // `site.name` is empty/falsy, so a "type the name" gate compares typed text
  // to '' (impossible to surface what to type, and a blank box would otherwise
  // match an empty name and let an accidental Enter through). When the name is
  // missing, fall back to the site ID as the confirm target — we display the
  // ID as the thing to type and match against it. Named sites still require
  // the exact, case-sensitive name.
  const hasName = typeof site.name === 'string' && site.name.trim().length > 0;
  const confirmTarget = hasName ? site.name : site.id;
  const confirmMatches = confirmText === confirmTarget;
  const reportCount = impact.reports.length;
  const sedCount = impact.seds.length;
  const sedDeletableCount = impact.seds.filter(s => s.willBeDeleted).length;
  const documentCount = impact.documents.length;
  const documentBytes = impact.documents.reduce((s, d) => s + (d.sizeBytes || 0), 0);
  const sharedSeds = impact.seds.filter(s => !s.willBeDeleted);

  const handleDelete = async () => {
    if (!confirmMatches || busy || approvalCheck.blocked) return;
    setBusy(true);

    // Approximate stage progress — the store action runs serially in this order.
    if (mode === 'with-everything') setStage('seds');
    else if (mode === 'with-reports') setStage('reports');
    else setStage('site');

    try {
      const result = await useStore.getState().deleteSite(site.id, mode);
      setStage('done');

      // Toast routing per spec:
      if (result.errors.length > 0) {
        toast.error(
          `Site deleted, but ${result.errors.length} file${result.errors.length === 1 ? '' : 's'} could not be removed. See admin log.`,
        );
      } else if (mode === 'site-only') {
        const n = reportCount;
        toast.success(
          `Site deleted.${n > 0 ? ` ${n} report${n === 1 ? ' was' : 's were'} left orphaned.` : ''}`,
          n > 0 && onCleanupOrphans ? {
            action: {
              label: 'Delete orphaned →',
              onClick: () => onCleanupOrphans(impact.reports.map(r => r.id)),
            },
          } : undefined,
        );
      } else if (mode === 'with-reports') {
        toast.success(`Site and ${result.deletedReports} report${result.deletedReports === 1 ? '' : 's'} deleted.`);
      } else {
        toast.success(
          `Site, ${result.deletedReports} report${result.deletedReports === 1 ? '' : 's'}, and ${formatBytes(result.storageFreedBytes)} of files deleted.`,
        );
      }

      onClose();
      onDeleted?.(result);
    } catch (e) {
      toast.error(`Delete failed: ${e instanceof Error ? e.message : 'unknown'}`);
      setBusy(false);
      setStage('idle');
    }
  };

  const ModeOption: React.FC<{ value: DeleteSiteMode; label: string; description: string; estBytes: number }> = ({ value, label, description, estBytes }) => (
    <label
      className={`flex items-start gap-2 p-2.5 border cursor-pointer transition-colors ${
        mode === value ? 'border-danger/40 bg-danger/[0.06]' : 'border-border-subtle hover:bg-state-hover'
      }`}
    >
      <input
        type="radio"
        name="delete-site-mode"
        value={value}
        checked={mode === value}
        onChange={() => setMode(value)}
        className="mt-0.5 accent-danger"
        disabled={busy}
      />
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2">
          <div className="type-section text-text-primary">{label}</div>
          <div className="type-body text-text-muted font-mono shrink-0">
            Frees ≈ {formatBytes(estBytes)}
          </div>
        </div>
        <div className="text-[10px] text-text-muted mt-0.5">{description}</div>
      </div>
    </label>
  );

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60" onClick={busy ? undefined : onClose}>
      <div className="bg-background w-[600px] max-h-[90vh] overflow-y-auto shadow-2xl border border-border" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="px-5 py-4 border-b border-border flex items-start gap-3">
          <div className="w-9 h-9 border border-danger/40 bg-danger/15 flex items-center justify-center shrink-0">
            <AlertTriangle className="w-4 h-4 text-danger" />
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-sm font-bold uppercase tracking-widest text-text-primary truncate">
              Delete site: {hasName ? site.name : '(unnamed site)'}
            </h2>
            <p className="text-[10px] text-text-secondary mt-0.5 truncate">
              {site.address || '—'} · {site.id}
              {(() => {
                // FIX #85 — a phantom site may have createdAt===undefined,
                // which makes `new Date(undefined)` render "Invalid Date".
                const created = site.createdAt ? new Date(site.createdAt) : null;
                return created && !Number.isNaN(created.getTime())
                  ? ` · created ${created.toLocaleDateString()}`
                  : '';
              })()}
            </p>
          </div>
          <button onClick={onClose} disabled={busy} className="p-1 text-text-muted hover:text-text-primary disabled:opacity-30">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Approval-workflow gate — refuses to render the action UI when blocked. */}
        {approvalCheck.loading && (
          <div className="px-5 py-6 text-[10px] text-text-muted uppercase tracking-widest">Checking approval workflows…</div>
        )}
        {!approvalCheck.loading && approvalCheck.blocked && (
          <div className="px-5 py-5 space-y-3">
            <div className="p-3 border border-danger/40 bg-danger/15 text-[10px] text-danger space-y-1">
              <div className="font-bold uppercase tracking-widest">Cannot delete: site has pending approval workflow</div>
              <div className="text-text-muted">
                Resolve the following report{approvalCheck.reportTitles.length === 1 ? '' : 's'} first:
              </div>
              <ul className="list-disc list-inside text-text-primary pt-1">
                {approvalCheck.reportTitles.map((t, i) => <li key={i} className="truncate">{t}</li>)}
              </ul>
            </div>
            <div className="flex justify-end">
              <Button variant="outline" size="sm" className="h-8 type-body uppercase tracking-widest" onClick={onClose}>Close</Button>
            </div>
          </div>
        )}

        {!approvalCheck.loading && !approvalCheck.blocked && (
          <>
            {/* Cascade preview */}
            <div className="px-5 py-3 border-b border-border-subtle bg-state-hover">
              <div className="flex items-start gap-2">
                <Building2 className="w-3.5 h-3.5 text-text-secondary mt-0.5 shrink-0" />
                <div className="flex-1">
                  <div className="type-body font-bold uppercase tracking-widest text-text-secondary mb-1.5">
                    What this site touches
                  </div>
                  <ul className="text-[10px] text-text-primary space-y-0.5 font-mono">
                    <li>1 site</li>
                    <li>
                      {reportCount} report{reportCount === 1 ? '' : 's'}
                      {impact.reportTypeLabels.length > 0 && (
                        <span className="text-text-muted"> ({impact.reportTypeLabels.join(', ')})</span>
                      )}
                    </li>
                    <li>
                      {sedCount} site evidence document{sedCount === 1 ? '' : 's'}
                      {sedDeletableCount < sedCount && (
                        <span className="text-text-muted"> ({sedDeletableCount} removable)</span>
                      )}
                    </li>
                    <li>
                      {documentCount} uploaded document{documentCount === 1 ? '' : 's'}
                      {documentBytes > 0 && <span className="text-text-muted"> ({formatBytes(documentBytes)})</span>}
                    </li>
                    <li>{impact.simulationCount} simulation{impact.simulationCount === 1 ? '' : 's'}</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Mode selector */}
            <div className="p-5 space-y-2">
              <div className="type-section text-text-secondary mb-2">Scope</div>
              <ModeOption
                value="site-only"
                label="Site only"
                description="Recommended for accidental creates. Reports stay (they'll appear orphaned in the dashboard)."
                estBytes={impact.bytesFreed.siteOnly}
              />
              <ModeOption
                value="with-reports"
                label="Site and all reports"
                description="Removes all reports for this site. Uploaded files (SEDs, documents) stay in storage."
                estBytes={impact.bytesFreed.withReports}
              />
              <ModeOption
                value="with-everything"
                label="Site and everything"
                description="Removes reports, evidence, uploaded documents, and simulations. Shared evidence is preserved."
                estBytes={impact.bytesFreed.withEverything}
              />
            </div>

            {/* Shared-SED callout (with-everything only) */}
            {mode === 'with-everything' && sharedSeds.length > 0 && (
              <div className="px-5 pb-3">
                <div className="p-3 border border-warning/40 bg-warning/15 text-[10px] space-y-1.5">
                  <div className="font-bold uppercase tracking-widest text-warning">
                    {sharedSeds.length} shared evidence document{sharedSeds.length === 1 ? '' : 's'} will be preserved
                  </div>
                  <div className="text-text-muted">
                    Storage estimate is conservative — these SEDs are referenced by reports for other sites:
                  </div>
                  <ul className="text-text-primary space-y-0.5 max-h-28 overflow-y-auto">
                    {sharedSeds.flatMap(s =>
                      s.preservedBy.map(r => (
                        <li key={`${s.sedId}-${r.id}`} className="truncate">
                          · <span className="font-mono">{s.sedId.slice(0, 10)}</span> ← {r.siteName}
                        </li>
                      )),
                    )}
                  </ul>
                </div>
              </div>
            )}

            {/* Typed confirmation */}
            <div className="px-5 pb-3 space-y-1.5">
              <label className="type-section text-text-secondary">
                {hasName ? 'Type the site name to confirm' : 'Type the site ID to confirm'}
              </label>
              {!hasName && (
                <div className="text-[10px] text-warning">
                  This site has no name (it was left by a failed creation). Type the ID below to delete it.
                </div>
              )}
              <div className="text-[10px] text-text-muted font-mono break-all bg-state-hover px-2 py-1 border border-border-subtle">
                {confirmTarget}
              </div>
              <input
                type="text"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder={hasName ? 'Type the name above (case-sensitive)' : 'Type the ID above (case-sensitive)'}
                className="w-full text-[11px] px-3 py-2 border border-border focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-1 focus:border-danger font-mono"
                autoFocus
                disabled={busy}
              />
            </div>

            {/* Inline progress */}
            {busy && stage !== 'idle' && stage !== 'done' && (
              <div className="px-5 pb-3 text-[10px] text-text-muted uppercase tracking-widest">
                {STAGE_LABEL[stage]}
              </div>
            )}

            {/* Footer */}
            <div className="px-5 py-3 border-t border-border flex items-center justify-end gap-2">
              <Button variant="outline" size="sm" className="h-8 type-body uppercase tracking-widest" onClick={onClose} disabled={busy} autoFocus>
                Cancel
              </Button>
              <Button
                variant="mission-danger"
                size="sm"
                className="h-8 type-body uppercase tracking-widest"
                disabled={!confirmMatches || busy}
                onClick={handleDelete}
              >
                {busy ? 'Deleting…' : 'Delete site'}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
